// Premium subscription helper functions

/**
 * Get display text for subscription status
 */
export function getSubscriptionStatusText(status) {
  const statusMap = {
    active: "Premium Active",
    expired: "Premium Expired",
    cancelled: "Subscription Cancelled",
    none: "Not Premium",
  };
  return statusMap[status] || "Unknown Status";
}

/**
 * Get display text for plan name with monthly cost
 */
export function getPlanDisplayName(plan) {
  const planMap = {
    "1-month": "1 Month Plan",
    "3-months": "3 Months Plan",
    "6-months": "6 Months Plan",
  };
  return planMap[plan] || "Unknown Plan";
}

/**
 * Get plan details including price and discount
 */
export function getPlanDetails(plan) {
  const plans = {
    "1-month": {
      name: "1 Month Premium",
      price: 3000,
      durationMonths: 1,
      discount: 0,
      monthlyRate: 3000,
      badge: null,
    },
    "3-months": {
      name: "3 Months Premium",
      price: 4500,
      durationMonths: 3,
      discount: 50,
      monthlyRate: 1500,
      badge: "50% OFF",
    },
    "6-months": {
      name: "6 Months Premium",
      price: 6000,
      durationMonths: 6,
      discount: 67,
      monthlyRate: 1000,
      badge: "BEST VALUE",
    },
  };
  return plans[plan];
}

/**
 * Format date for display
 */
export function formatSubscriptionDate(date) {
  if (!date) return "N/A";
  try {
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "Invalid Date";
  }
}

/**
 * Calculate days remaining until expiry
 */
export function getDaysRemaining(expiryDate) {
  if (!expiryDate) return 0;
  try {
    const now = new Date();
    const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
    const diff = expiry - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  } catch (e) {
    return 0;
  }
}

/**
 * Get status badge color
 */
export function getStatusColor(status) {
  const colors = {
    active: "bg-green-100 text-green-800 border-green-300",
    expired: "bg-red-100 text-red-800 border-red-300",
    cancelled: "bg-gray-100 text-gray-800 border-gray-300",
    none: "bg-yellow-100 text-yellow-800 border-yellow-300",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
}

/**
 * Check if should show renewal reminder
 */
export function shouldShowRenewalReminder(daysRemaining) {
  return daysRemaining > 0 && daysRemaining <= 10;
}

/**
 * Get renewal reminder message
 */
export function getRenewalReminderMessage(daysRemaining) {
  if (daysRemaining === 1) return "Your premium expires tomorrow!";
  if (daysRemaining <= 3) return `Your premium expires in ${daysRemaining} days`;
  if (daysRemaining <= 7) return `Your premium expires in ${daysRemaining} days`;
  return `Your premium expires in ${daysRemaining} days`;
}

/**
 * Check if premium status is active
 */
export function isPremiumActive(subscriptionEndDate) {
  if (!subscriptionEndDate) return false;
  try {
    const expiryDate = subscriptionEndDate.toDate ? subscriptionEndDate.toDate() : new Date(subscriptionEndDate);
    return expiryDate > new Date();
  } catch (e) {
    return false;
  }
}

/**
 * Get premium features list
 */
export function getPremiumFeatures() {
  return [
    {
      icon: "👁️",
      title: "See Contact Details",
      description: "View phone numbers of other premium users",
    },
    {
      icon: "📱",
      title: "Share Your Contact",
      description: "Let others see your contact information",
    },
    {
      icon: "⭐",
      title: "Premium Badge",
      description: "Stand out with a premium indicator on your profile",
    },
    {
      icon: "⏱️",
      title: "Flexible Plans",
      description: "1, 3, or 6 month plans - choose what suits you",
    },
  ];
}
